/* eslint-disable react/no-multi-comp */
import { Money } from "@upserve/financials";
import { isEmpty } from "ramda";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { TrackJS } from "trackjs";
import uuid from "uuid/v4";

import { DigitalGiftCardCreditCardForm as CreditCardForm } from "./DigitalGiftCardCreditCardForm";
import type { MarginProps } from "components/digital_gift_cards/header";
import OrderForm, {
  type DigitalGiftCardOrderForm,
} from "components/digital_gift_cards/order_form";

import type { ICreditCardPostVaultingProps } from "types/credit_card";
import type {
  DigitalGiftCardOrderApiResponse,
  DigitalGiftCardsSettings,
  SubmitGiftCardCreditCardPaymentProps,
} from "types/digital_gift_cards";
import type { RemoteData, RequestStatus } from "types/requests";

const DefaultGiftCard = require("images/default-gift-card.png");

export const StyledDGCMainContent = styled.div<MarginProps>`
  width: 850px;
  margin-left: ${({ leftMargin }) => leftMargin};
  display: flex;

  @media (max-width: 1024px) {
    margin-left: 20px;
    margin-right: 20px;
    width: auto;
    padding: 12px;
    flex-direction: column-reverse;
  }
`;

export const StyledFormWrapper = styled.div`
  width: 424px;
  padding: 0 32px;

  @media (max-width: 1024px) {
    width: auto;
  }
`;

export const StyledGiftCardOutline = styled.div`
  margin-top: 32px;
  width: 360px;
  height: 224px;
  display: flex;
  justify-content: center;
  align-items: center;

  background: #ffffff;
  border: 2px solid #f6f6f6;
  box-shadow: 2px 5px 5px #888888;
  box-sizing: border-box;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  img {
    max-height: 90%;
    max-width: 90%;
    object-fit: contain;
  }

  @media (max-width: 1024px) {
    width: 270px;
    height: 165px;
    margin: auto;
    margin-bottom: 32px;
  }
`;

export const StyledDefaultGiftCardWrapper = styled.div`
  margin-top: 32px;
  width: 360px;
  height: 224px;

  img {
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 1024px) {
    width: 270px;
    height: 165px;
    margin: auto;
    margin-bottom: 32px;
  }
`;

type MainContentProps = {
  settings: DigitalGiftCardsSettings;
  leftMargin: string;
  afterProcessedPaymentRequest: RemoteData<DigitalGiftCardOrderApiResponse>;
  creditCardRequestStatus: RequestStatus;
  onSubmitCreditCardPayment: (
    props: SubmitGiftCardCreditCardPaymentProps
  ) => void;
  onSetCreditCardRequestStatus: (props: RequestStatus) => void;
};

export default function MainContent({
  settings,
  leftMargin,
  afterProcessedPaymentRequest,
  creditCardRequestStatus,
  onSubmitCreditCardPayment,
  onSetCreditCardRequestStatus,
}: MainContentProps) {
  const { logoUrl, storePrettyUrl, ugMerchantKey } = settings;

  // new uuid will generate on page reload.
  const [sessionId] = useState(uuid());
  useEffect(() => {
    TrackJS.configure({ sessionId });
  }, [sessionId]);

  const [orderFormValid, setOrderFormValid] = useState(false);
  const [orderFormValues, setOrderFormValues] =
    useState<DigitalGiftCardOrderForm>();

  const giftCardImage = logoUrl.match({
    Just: url => (
      <StyledGiftCardOutline>
        <img src={url} alt="gift card" />
      </StyledGiftCardOutline>
    ),
    Nothing: () => (
      <StyledDefaultGiftCardWrapper>
        <img src={DefaultGiftCard} alt="gift card" />
      </StyledDefaultGiftCardWrapper>
    ),
  });

  const onFinalSubmission = (
    paymentSubmissionInfo: ICreditCardPostVaultingProps
  ) => {
    return onSubmitCreditCardPayment({
      sessionId,
      storePrettyUrl,
      ...paymentSubmissionInfo,
      formInfo: orderFormValues as DigitalGiftCardOrderForm,
    });
  };

  const balanceDue =
    orderFormValues &&
    !isEmpty(orderFormValues.amount) &&
    !Number.isNaN(parseFloat(orderFormValues.amount))
      ? Money.fromDollars(orderFormValues.amount)
      : Money(0);

  return (
    <StyledDGCMainContent leftMargin={leftMargin}>
      <StyledFormWrapper>
        <OrderForm
          settings={settings}
          setFormValid={setOrderFormValid}
          setFormValues={setOrderFormValues}
        />
        <CreditCardForm<DigitalGiftCardOrderApiResponse>
          merchantInfo={{ ugMerchantKey, prettyUrl: storePrettyUrl }}
          paymentInfo={{ balanceDue }}
          afterProcessedPaymentRequest={afterProcessedPaymentRequest}
          creditCardRequestStatus={creditCardRequestStatus}
          submitCreditCardPayment={onFinalSubmission}
          setCreditCardRequestStatus={onSetCreditCardRequestStatus}
          externalSubmitDisabled={!orderFormValid || balanceDue.isZero()}
        />
      </StyledFormWrapper>
      {giftCardImage}
    </StyledDGCMainContent>
  );
}
